import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waitlist() {
  const [form, setForm] = useState({ name: "", email: "", product: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/early-access`, form);
      setDone(true);
      toast.success("You're on the list. Welcome to the Kingdom.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-white/5 px-5 py-3.5 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors duration-300 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]";

  return (
    <section id="access" data-testid="waitlist-section" className="relative border-t border-white/10">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 55% 60% at 50% 100%, rgba(212,175,55,0.10), transparent 70%)",
        }}
      />
      <div className="relative mx-auto max-w-3xl px-6 py-24 text-left lg:py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#D4AF37]">
            Early Access
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Build your AI business development department.
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400">
            Phase A opens with the Podcast and YouTube engines. Join the founding cohort and be
            first to run the full Blueprint on your own product.
          </p>

          {done ? (
            <div
              data-testid="waitlist-success"
              className="mt-10 flex items-start gap-4 border border-[#047857]/40 bg-[#047857]/10 p-6"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#10B981]" />
              <div>
                <p className="font-semibold text-white">You&rsquo;re on the founding list.</p>
                <p className="mt-1 text-sm text-zinc-400">
                  We&rsquo;ll reach out as Phase A access opens. Serve first, sell second.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="waitlist-form" className="mt-10 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  required
                  type="text"
                  placeholder="Your name"
                  data-testid="waitlist-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  placeholder="Work email"
                  data-testid="waitlist-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={inputCls}
                />
              </div>
              <input
                type="text"
                placeholder="What product are you looking to distribute? (optional)"
                data-testid="waitlist-product-input"
                value={form.product}
                onChange={(e) => setForm({ ...form, product: e.target.value })}
                className={inputCls}
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit-button"
                className="group inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:scale-105 hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Join the Founding Cohort
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-500">
                No spam — that would rather defeat the point.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
